import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { X, Camera, SwitchCamera, Flashlight } from 'lucide-react';

export function BarcodeScanner({ onScan, onClose, isOpen }) {
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [currentCamera, setCurrentCamera] = useState(0);
    const scannerRef = useRef(null);
    const html5QrcodeRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            initScanner();
        }
        return () => {
            stopScanner();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const initScanner = async () => {
        try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length > 0) {
                setCameras(devices);
                // Prefer back camera
                const backCamera = devices.findIndex(d => 
                    d.label.toLowerCase().includes('back') || 
                    d.label.toLowerCase().includes('rear')
                );
                setCurrentCamera(backCamera >= 0 ? backCamera : 0);
                startScanner(devices[backCamera >= 0 ? backCamera : 0].id);
            } else {
                setError('No cameras found');
            }
        } catch (err) {
            setError('Camera access denied. Please allow camera permissions.');
            console.error('Camera error:', err);
        }
    };

    const startScanner = async (cameraId) => {
        if (html5QrcodeRef.current) {
            await stopScanner();
        }

        html5QrcodeRef.current = new Html5Qrcode('barcode-scanner');
        
        try {
            await html5QrcodeRef.current.start(
                cameraId,
                {
                    fps: 10,
                    qrbox: { width: 250, height: 150 },
                    aspectRatio: 1.0
                },
                (decodedText) => {
                    // Vibrate on successful scan
                    if (navigator.vibrate) {
                        navigator.vibrate(200);
                    }
                    onScan(decodedText);
                },
                (errorMessage) => {
                    // Ignore scan errors (normal when no barcode in view)
                }
            );
            setIsScanning(true);
            setError(null);
        } catch (err) {
            setError('Failed to start scanner');
            console.error('Scanner error:', err);
        }
    };

    const stopScanner = async () => {
        if (html5QrcodeRef.current && html5QrcodeRef.current.isScanning) {
            try {
                await html5QrcodeRef.current.stop();
                html5QrcodeRef.current.clear();
            } catch (err) {
                console.error('Error stopping scanner:', err);
            }
        }
        setIsScanning(false);
    };

    const switchCamera = async () => {
        if (cameras.length <= 1) return;
        const nextCamera = (currentCamera + 1) % cameras.length;
        setCurrentCamera(nextCamera);
        await startScanner(cameras[nextCamera].id);
    };

    const handleClose = async () => {
        await stopScanner();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 text-white">
                <h2 className="text-lg font-semibold">Scan Barcode</h2>
                <div className="flex gap-2">
                    {cameras.length > 1 && (
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={switchCamera}
                            className="text-white hover:bg-white/20"
                            data-testid="switch-camera-btn"
                        >
                            <SwitchCamera className="h-5 w-5" />
                        </Button>
                    )}
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={handleClose}
                        className="text-white hover:bg-white/20"
                        data-testid="close-scanner-btn"
                    >
                        <X className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            {/* Scanner Area */}
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="relative w-full max-w-md">
                    {error ? (
                        <Card className="bg-red-500/20 border-red-500">
                            <CardContent className="p-6 text-center text-white">
                                <Camera className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>{error}</p>
                                <Button 
                                    onClick={initScanner} 
                                    className="mt-4"
                                    variant="secondary"
                                >
                                    Try Again
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            <div 
                                id="barcode-scanner" 
                                ref={scannerRef}
                                className="w-full rounded-lg overflow-hidden"
                            />
                            {/* Scan guide overlay */}
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                <div className="w-64 h-32 border-2 border-emerald-500 rounded-lg relative">
                                    <div className="absolute -top-1 -left-1 w-4 h-4 border-t-4 border-l-4 border-emerald-500 rounded-tl" />
                                    <div className="absolute -top-1 -right-1 w-4 h-4 border-t-4 border-r-4 border-emerald-500 rounded-tr" />
                                    <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-4 border-l-4 border-emerald-500 rounded-bl" />
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-4 border-r-4 border-emerald-500 rounded-br" />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Instructions */}
            <div className="p-4 text-center text-white/70">
                <p className="text-sm">Position the barcode within the frame</p>
                <p className="text-xs mt-1">Supports UPC, EAN, Code 128, QR codes</p>
            </div>
        </div>
    );
}
